<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: String, default: '' },
  min: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  // Overrides the default chrome of the trigger button so the picker can sit
  // seamlessly inside a segmented hero-search cell (borderless variant).
  triggerClass: {
    type: [String, Object, Array],
    default:
      'h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 hover:border-n-slate-6 focus:border-n-brand',
  },
  // Compact trigger label ("24 Jun" instead of "Wed, 24 Jun 2026") so two
  // dates fit side by side inside a narrow hero-search cell.
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useI18n();

// Format a Date to "YYYY-MM-DD" from local y/m/d parts (never via UTC, to
// avoid timezone day-shift). Also used to compare against `min`.
const toYmd = date => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Parse a "YYYY-MM-DD" string into a local Date (noon to dodge DST edges).
const fromYmd = value => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const isOpen = ref(false);
const jumpMode = ref(false);
const rootRef = ref(null);

// The month currently displayed in the grid (a Date pinned to day 1).
const viewDate = ref(new Date());

const todayYmd = computed(() => toYmd(new Date()));

const initView = () => {
  const base = fromYmd(props.modelValue) || fromYmd(props.min) || new Date();
  viewDate.value = new Date(base.getFullYear(), base.getMonth(), 1, 12);
};

const triggerLabel = computed(() => {
  const date = fromYmd(props.modelValue);
  if (!date) return '';
  const options = props.compact
    ? { day: '2-digit', month: 'short' }
    : { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat(locale.value, options).format(date);
});

const monthYearLabel = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    month: 'long',
    year: 'numeric',
  }).format(viewDate.value)
);

const weekdayLabels = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value, { weekday: 'short' });
  // 2023-01-01 is a Sunday; week starts Sunday.
  return Array.from({ length: 7 }, (_, i) =>
    fmt.format(new Date(2023, 0, 1 + i, 12))
  );
});

const monthShortLabels = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value, { month: 'short' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2023, i, 1)));
});

const days = computed(() => {
  const year = viewDate.value.getFullYear();
  const month = viewDate.value.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(year, month, d, 12));
  }
  return cells;
});

const isDisabled = date => Boolean(props.min) && toYmd(date) < props.min;
const isSelected = date => toYmd(date) === props.modelValue;
const isToday = date => toYmd(date) === todayYmd.value;

const open = () => {
  initView();
  jumpMode.value = false;
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
  jumpMode.value = false;
};

const toggle = () => (isOpen.value ? close() : open());

const prevMonth = () => {
  viewDate.value = new Date(
    viewDate.value.getFullYear(),
    viewDate.value.getMonth() - 1,
    1,
    12
  );
};

const nextMonth = () => {
  viewDate.value = new Date(
    viewDate.value.getFullYear(),
    viewDate.value.getMonth() + 1,
    1,
    12
  );
};

const prevYear = () => {
  viewDate.value = new Date(
    viewDate.value.getFullYear() - 1,
    viewDate.value.getMonth(),
    1,
    12
  );
};

const nextYear = () => {
  viewDate.value = new Date(
    viewDate.value.getFullYear() + 1,
    viewDate.value.getMonth(),
    1,
    12
  );
};

const pickMonth = monthIndex => {
  viewDate.value = new Date(viewDate.value.getFullYear(), monthIndex, 1, 12);
  jumpMode.value = false;
};

const selectDate = date => {
  if (isDisabled(date)) return;
  emit('update:modelValue', toYmd(date));
  close();
};

// Footer shortcuts. Clamp to `min` if the shortcut lands before it.
const applyShortcut = offsetDays => {
  const base = new Date();
  base.setDate(base.getDate() + offsetDays);
  let ymd = toYmd(base);
  if (props.min && ymd < props.min) ymd = props.min;
  emit('update:modelValue', ymd);
  close();
};

const onDocumentClick = event => {
  if (rootRef.value && !rootRef.value.contains(event.target)) close();
};

const onKeydown = event => {
  if (event.key === 'Escape' && isOpen.value) close();
};

watch(isOpen, value => {
  if (value) {
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onKeydown);
  } else {
    document.removeEventListener('click', onDocumentClick, true);
    document.removeEventListener('keydown', onKeydown);
  }
});

onMounted(initView);

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick, true);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      class="flex items-center gap-2 text-sm text-n-slate-12 outline-none transition-colors"
      :class="triggerClass"
      @click="toggle"
    >
      <span class="i-lucide-calendar text-base text-n-slate-11" />
      <span v-if="triggerLabel" class="truncate">{{ triggerLabel }}</span>
      <span v-else class="truncate text-n-slate-10">
        {{ placeholder || t('CONVERSATION.CAR_BOOKING.TRIP.DATE_PLACEHOLDER') }}
      </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute top-full left-0 z-50 mt-1 w-[280px] rounded-lg border border-n-weak bg-n-solid-1 p-3 shadow-lg"
    >
      <div class="mb-2 flex items-center justify-between">
        <button
          type="button"
          class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
          @click="jumpMode ? prevYear() : prevMonth()"
        >
          <span class="i-lucide-chevron-left text-base" />
        </button>
        <button
          type="button"
          class="rounded-md px-2 py-1 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
          @click="jumpMode = !jumpMode"
        >
          {{ jumpMode ? viewDate.getFullYear() : monthYearLabel }}
        </button>
        <button
          type="button"
          class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
          @click="jumpMode ? nextYear() : nextMonth()"
        >
          <span class="i-lucide-chevron-right text-base" />
        </button>
      </div>

      <div v-if="jumpMode" class="grid grid-cols-3 gap-1.5">
        <button
          v-for="(label, index) in monthShortLabels"
          :key="index"
          type="button"
          class="rounded-md py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          :class="{
            'bg-n-brand text-white hover:bg-n-brand':
              index === viewDate.getMonth(),
          }"
          @click="pickMonth(index)"
        >
          {{ label }}
        </button>
      </div>

      <template v-else>
        <div class="mb-1 grid grid-cols-7 gap-0.5">
          <span
            v-for="(label, index) in weekdayLabels"
            :key="index"
            class="py-1 text-center text-xs font-medium text-n-slate-10"
          >
            {{ label }}
          </span>
        </div>
        <div class="grid grid-cols-7 gap-0.5">
          <template v-for="(date, index) in days" :key="index">
            <span v-if="!date" />
            <button
              v-else
              type="button"
              :disabled="isDisabled(date)"
              class="flex h-8 items-center justify-center rounded-md text-sm transition-colors"
              :class="[
                isSelected(date)
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-12 hover:bg-n-alpha-2',
                isDisabled(date)
                  ? 'cursor-not-allowed text-n-slate-8 hover:bg-transparent'
                  : '',
                isToday(date) && !isSelected(date)
                  ? 'ring-1 ring-inset ring-n-brand'
                  : '',
              ]"
              @click="selectDate(date)"
            >
              {{ date.getDate() }}
            </button>
          </template>
        </div>
      </template>

      <div class="mt-3 flex items-center gap-1.5 border-t border-n-weak pt-3">
        <button
          type="button"
          class="rounded-md bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-12 hover:bg-n-alpha-2"
          @click="applyShortcut(0)"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.TODAY') }}
        </button>
        <button
          type="button"
          class="rounded-md bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-12 hover:bg-n-alpha-2"
          @click="applyShortcut(7)"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.NEXT_WEEK') }}
        </button>
        <button
          type="button"
          class="rounded-md bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-12 hover:bg-n-alpha-2"
          @click="applyShortcut(30)"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.NEXT_MONTH') }}
        </button>
      </div>
    </div>
  </div>
</template>
